import React, { useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { volumePages } from '../data/volumePages';
import { useSlideTrack, SLIDE_EASE } from '../hooks/useSlideTrack';
import { PageLightbox } from './PageLightbox';
const arrowClass = 'absolute z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-[#E7E3DB] bg-white text-[#15213A] shadow-[0_6px_18px_-8px_rgba(21,33,58,0.35)] transition-[background-color,opacity] duration-150 ease-out hover:bg-[#FDF0D5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4B23E] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FAF6EF] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white lg:flex';
export function PageCarousel() {
  const total = volumePages.length;
  const {
    viewportRef,
    index,
    goTo,
    x,
    gap,
    slideWidth,
    isDesktop,
    panHandlers,
    wasDragged
  } = useSlideTrack(total);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  if (total === 0) return null;
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goTo(index - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goTo(index + 1);
    }
  };
  const imageMid = slideWidth * 16 / 9 / 2;
  return <section aria-labelledby="pages-heading" className="w-full overflow-hidden bg-[#FAF6EF]" data-id="element-147" data-mp="2gkinl9ek6ozy">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-24" data-id="element-148" data-mp="2br6anwj8s6n1">
        <header className="mx-auto max-w-2xl text-center" data-id="element-149" data-mp="26uftf5vpb501">
          <h2 id="pages-heading" className="font-poppins text-[28px] font-semibold leading-[1.15] tracking-[-0.01em] text-[#15213A] lg:text-[40px]" data-id="element-150" data-mp="21ubehkafyibx">
            Take a look inside Volume 4
          </h2>
          <p className="mt-3 text-base leading-relaxed text-[#5C6675] lg:text-lg" data-id="element-151" data-mp="1suo9ypmf0wen">
            Every page is a session you can open and teach in minutes.
          </p>
        </header>

        <div className="relative mt-10 lg:mt-14" role="region" aria-roledescription="carousel" aria-label="Pages from Volume 4" onKeyDown={onKeyDown} data-id="element-152" data-mp="1n7urh98eh8g7">
          <button type="button" onClick={() => goTo(index - 1)} disabled={index === 0} aria-label="Previous page" className={`${arrowClass} left-0`} style={{
          top: imageMid
        }} data-id="element-153" data-mp="1e7xvemjol7b4">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-154" data-mp="1s6p7k3ezgiwm" />
          </button>

          <div ref={viewportRef} className="overflow-hidden lg:mx-16" data-id="element-155" data-mp="1s4uvzfplq357">
            <motion.ul {...panHandlers} style={{
            x,
            gap,
            touchAction: 'pan-y'
          }} className="flex cursor-grab select-none items-start active:cursor-grabbing" data-id="element-156" data-mp="1x47ghtt02y4m">
              {volumePages.map((page, i) => {
              const active = i === index;
              const dimmed = isDesktop && !active;
              return <motion.li key={page.src} aria-roledescription="slide" aria-label={`${i + 1} of ${total}`} aria-hidden={!active && !isDesktop ? true : undefined} className="flex-none" style={{
                width: slideWidth
              }} animate={{
                scale: dimmed ? 0.84 : 1,
                opacity: dimmed ? 0.7 : 1
              }} transition={{
                duration: 0.3,
                ease: SLIDE_EASE
              }} data-id="element-157" data-mp="2uyibxci6ohwd" data-mpi={i}>
                    <button type="button" tabIndex={active ? 0 : -1} onClick={() => {
                  if (wasDragged()) return;
                  if (active) setLightboxIndex(i);else goTo(i);
                }} aria-label={active ? `Open full screen: ${page.caption}` : `Show page ${i + 1}`} className="block w-full overflow-hidden rounded-2xl border border-[#E7E3DB] bg-white shadow-[0_1px_2px_rgba(21,33,58,0.05),0_14px_32px_-14px_rgba(21,33,58,0.28)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4B23E] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FAF6EF]" data-id="element-158" data-mp="2q1ruakqqde8u">
                      <img src={page.src} alt={page.caption} loading="lazy" decoding="async" draggable={false} className="pointer-events-none aspect-[9/16] w-full object-contain" data-id="element-159" data-mp="2oyszwx65677g" />
                    </button>
                    <p className="mt-4 px-1 text-[15px] leading-relaxed text-[#5C6675] lg:text-center" aria-hidden="true" data-id="element-160" data-mp="37ospdl5w9obe">
                      {page.caption}
                    </p>
                  </motion.li>;
            })}
            </motion.ul>
          </div>

          <button type="button" onClick={() => goTo(index + 1)} disabled={index === total - 1} aria-label="Next page" className={`${arrowClass} right-0`} style={{
          top: imageMid
        }} data-id="element-161" data-mp="w15mgm0b7t2e">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-162" data-mp="n223di157dnw" />
          </button>
        </div>

        <div className="mt-8 flex items-center justify-center gap-4" data-id="element-163" data-mp="1jpymatk1yl2a">
          <div className="flex items-center gap-1" role="group" aria-label="Choose a page" data-id="element-164" data-mp="1fq7nc2kdgymo">
            {volumePages.map((page, i) => <button key={page.src} type="button" onClick={() => goTo(i)} aria-label={`Go to page ${i + 1}`} aria-current={i === index ? 'true' : undefined} className="flex h-8 min-w-[24px] items-center justify-center rounded-full px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F4B23E]" data-id="element-165" data-mp="1awbufeiyu1yd" data-mpi={i}>
                <span className={`block h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out ${i === index ? 'w-6 bg-[#F4B23E]' : 'w-2.5 bg-[#E7E3DB]'}`} data-id="element-166" data-mp="2mj2s3vt87fpz" />
              </button>)}
          </div>
          <p className="text-sm tabular-nums text-[#5C6675]" aria-live="polite" data-id="element-167" data-mp="1yu1d1ppw16bq">
            <span className="font-semibold text-[#15213A]" data-id="element-168" data-mp="23sut9pwk7bfl">{index + 1}</span> / {total}
          </p>
        </div>
      </div>

      <PageLightbox pages={volumePages} index={lightboxIndex} onIndexChange={i => {
      setLightboxIndex(i);
      goTo(i);
    }} onClose={closeLightbox} data-id="element-169" data-mp="1ptlaqjkvs58g" />
    </section>;
}